import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Button, Pill, SkeletonSet } from '@cwos/ui-admin';
import { LoadFailure } from '../LoadFailure.js';
import { MutationFailure } from '../../lib/mutationFailure.js';
import { insights, type FindingDetail } from './insightsClient.js';
import { OwnerPanel } from './OwnerPanel.js';
import { EscalationPanel } from './EscalationPanel.js';
import { ApplicabilityPanel } from './ApplicabilityPanel.js';
import {
  applicabilitySentence,
  contextOf,
  entitySentence,
  eventLabel,
  pillVariantFor,
  recurrenceSentence,
  severityLabel,
  subjectsOf,
} from './insightsLogic.js';

/**
 * ONE FINDING, in the order the directive asks for it:
 *
 *   what Phibian noticed · why it matters · evidence · what changed ·
 *   what may happen next · recommended action · owner · related policy ·
 *   related tasks · agent action · outcome history
 *
 * The order is not decoration. A reader arriving here has one question —
 * "should I do something about this?" — and answering it requires, in this
 * sequence: the claim, the reason it is a claim, the numbers behind it, and
 * only then a suggestion. A page that opened with the suggestion would be
 * asking for compliance rather than judgement, which is the opposite of
 * "encourage investigation".
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY NOT ON THIS PAGE
 * ---------------------------------------------------------------------------
 * A FORECAST. "What may happen next" is B6's, and it is not built. The section
 * says so in the product's own vocabulary — NOT YET OBSERVED — rather than
 * being quietly absent, because an absent section reads as "there is nothing
 * to say" and there is: nobody has built the thing that would say it.
 *
 * AN OWNERSHIP LINE THAT READS AS TWO STATES. Ownership is `OwnerPanel`'s, and
 * it has three: a named person, a responsible organizational role, and nobody
 * having decided. The `finding.ownerUserId ? 'Assigned' : 'Nobody has taken
 * this on'` this page used to carry could tell none of them apart, and told a
 * reader that a role-owned finding was unowned.
 *
 * A PARTICIPANT NAME THIS CALLER MAY NOT SEE. Every entity is rendered through
 * `entitySentence`, which keeps the server's three answers as three sentences.
 * "Not shown to you" and "No longer in the records" are different facts and
 * only one of them is a withholding.
 */

export function InsightDetail() {
  const params = useParams({ strict: false }) as { findingId?: string };
  const findingId = params.findingId ?? '';
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);

  const detail = useQuery({
    queryKey: ['insights', 'finding', findingId],
    queryFn: () => insights.getFinding(findingId),
    enabled: findingId !== '',
  });

  const invalidate = (updated: FindingDetail): void => {
    queryClient.setQueryData(['insights', 'finding', findingId], updated);
    void queryClient.invalidateQueries({ queryKey: ['insights', 'findings'] });
  };

  const acknowledge = useMutation({
    mutationFn: () => insights.acknowledge(findingId),
    onSuccess: invalidate,
  });

  const dismiss = useMutation({
    mutationFn: () => insights.dismiss(findingId, reason),
    onSuccess: (updated) => {
      setReason('');
      setReasonTouched(false);
      invalidate(updated);
    },
  });

  if (detail.isLoading) return <SkeletonSet surface="drawer" rows={8} />;
  if (detail.isError) {
    return (
      <LoadFailure
        error={detail.error}
        message="We could not load this finding."
        onRetry={() => void detail.refetch()}
        mainRead
        refusedHeading="You cannot open this finding"
      />
    );
  }

  const finding = detail.data;
  if (!finding) return null;

  const reasonTooShort = reason.trim().length < 3;

  return (
    <article className="rn-col" style={{ gap: 'var(--space-16)' }}>
      <a className="rn-link" href="/insights">
        ← Insights
      </a>

      {/* 1. what Phibian noticed */}
      <header className="rn-col" style={{ gap: 'var(--space-8)' }}>
        <div className="rn-row" style={{ gap: 'var(--space-12)', alignItems: 'center' }}>
          <h1>{finding.title}</h1>
          <Pill variant={pillVariantFor(finding.severity)}>{severityLabel(finding.severity)}</Pill>
        </div>
        <p className="rn-muted">
          {subjectsOf(finding.entities).map(entitySentence).join(' · ') || 'No subject recorded'}
        </p>
      </header>

      {/* 2. why it matters */}
      <section className="rn-card">
        <h2 className="rn-card-title">Why it matters</h2>
        <p>{finding.explanation}</p>
        <p className="rn-muted">
          {recurrenceSentence(finding.recurrenceCount)} · first noticed{' '}
          <time dateTime={finding.discoveredAt}>
            {new Date(finding.discoveredAt).toLocaleDateString()}
          </time>{' '}
          · last confirmed{' '}
          <time dateTime={finding.lastSeenAt}>
            {new Date(finding.lastSeenAt).toLocaleDateString()}
          </time>
        </p>
      </section>

      {/* 3 + 4. evidence, which IS what changed */}
      <section className="rn-card">
        <h2 className="rn-card-title">Evidence</h2>
        <p className="rn-muted">
          The readings this finding was derived from, oldest first. Nothing here is a summary
          somebody wrote — these are the numbers the detector actually examined.
        </p>
        {finding.evidence.length === 0 ? (
          <p className="rn-muted">No windowed reading was cited. The shortfall is the finding.</p>
        ) : (
          <ul className="rn-list">
            {finding.evidence.map((row) => (
              <li key={`${row.refIndex}-${row.windowStart}`}>
                <span className="rn-strong">
                  {row.value} {row.unit}
                </span>{' '}
                <span className="rn-muted">
                  {row.metricKey} · window ending{' '}
                  <time dateTime={row.windowEnd}>
                    {new Date(row.windowEnd).toLocaleDateString()}
                  </time>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 5. what may happen next — absent, and it says so */}
      <section className="rn-card">
        <h2 className="rn-card-title">What may happen next</h2>
        <p className="rn-muted">
          Phibian does not forecast this yet. It is watching the condition and will say when the
          reading moves; it is not predicting where it goes.
        </p>
      </section>

      {/* 6. recommended action */}
      <section className="rn-card">
        <h2 className="rn-card-title">What you might do</h2>
        {finding.actions.length === 0 ? (
          <p className="rn-muted">No recommendation is attached to this finding.</p>
        ) : (
          <ul className="rn-list">
            {finding.actions.map((action) => (
              <li key={action.ordinal}>
                <span className="rn-strong">{action.label}</span>
                {action.rationale ? <span className="rn-muted"> — {action.rationale}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 7. owner — three states, and the act of deciding between them */}
      <OwnerPanel finding={finding} />

      {/*
        7b. ESCALATION — which is not a button, and sits under the owner because
        it is the question you ask about a finding whose owner is not enough.
        Ruling 5's three answers are rendered as three answers; a screen that
        collapsed them into "escalated" would have thrown away the only part of
        the ladder a customer can see.
      */}
      <EscalationPanel findingId={finding.id} />

      {/* 8. related policy, 9/10. related tasks and agent action */}
      <section className="rn-card">
        <h2 className="rn-card-title">Context</h2>
        <dl className="rn-facts">
          <dt>Related policy</dt>
          <dd>{finding.findingType}</dd>
          <dt>Also touches</dt>
          <dd>{contextOf(finding.entities).map(entitySentence).join(' · ') || 'Nothing else'}</dd>
          <dt>Related work</dt>
          <dd>
            {finding.links.length === 0
              ? 'No task, agent run or outcome is linked yet'
              : finding.links.map((link) => link.linkKind).join(' · ')}
          </dd>
        </dl>
      </section>

      {/* 10b. WHY THIS IS OR IS NOT SILENT.
           Always rendered, never only when suppressed. A screen that showed
           this section only for a `not_applicable` finding would make the
           OTHER four answers invisible — and the four include "a person
           looked and could not tell" and "two determinations disagree", both
           of which are things a reader needs to know about a finding that IS
           being reported. */}
      <section className="rn-card">
        <h2 className="rn-card-title">Whether this requirement governs you</h2>
        <p>{applicabilitySentence(finding.applicability)}</p>
        {finding.applicabilityDeterminations.length === 0 ? null : (
          <ul className="rn-list">
            {finding.applicabilityDeterminations.map((determination) => (
              <li key={determination.id}>
                <span className="rn-strong">
                  {determination.scope.kind === 'org'
                    ? 'Organization-wide'
                    : determination.scope.kind === 'program'
                      ? 'This program'
                      : 'This location'}
                </span>{' '}
                <span className="rn-muted">
                  · recorded{' '}
                  <time dateTime={determination.decidedAt}>
                    {new Date(determination.decidedAt).toLocaleDateString()}
                  </time>
                </span>
                <p>{determination.basis}</p>
                {/* The SOURCE, shown separately from the reasoning. A
                    suppression the server accepted always has one; showing
                    them merged would let a determination read as sourced when
                    only its rationale was written. */}
                <p className="rn-muted">
                  {determination.basisSource === null
                    ? 'No authority was cited. This determination does not silence anything.'
                    : `Relying on: ${determination.basisSource}`}
                </p>
              </li>
            ))}
          </ul>
        )}
        {/* THE ACT, BENEATH THE ANSWER. Reading why a finding is silent
            charges `insights:read`; ruling on it charges
            `insights:applicability`, which since founder closeout ruling 5 of
            2026-09-08 is a real capability seeded to org_admin. The panel
            renders its own four-state gate, so a reader without the key sees
            the determinations above and a sentence about the act, never a
            control that would always refuse. */}
        <ApplicabilityPanel finding={finding} />
      </section>

      {/* 11. outcome history — the append-only record */}
      <section className="rn-card">
        <h2 className="rn-card-title">History</h2>
        <ul className="rn-list">
          {finding.events.map((event) => (
            <li key={event.id}>
              <span className="rn-strong">{eventLabel(event.kind, event.resolutionReason)}</span>{' '}
              <span className="rn-muted">
                <time dateTime={event.occurredAt}>
                  {new Date(event.occurredAt).toLocaleString()}
                </time>
                {event.actorKind === 'user' ? ' · by a person' : ' · by Phibian'}
              </span>
              {event.reason ? <p>{event.reason}</p> : null}
            </li>
          ))}
        </ul>
      </section>

      {/* The two decisions a person can record. */}
      {finding.state === 'needs_attention' || finding.state === 'watching' ? (
        <section className="rn-card">
          <h2 className="rn-card-title">Record a decision</h2>
          {finding.state === 'needs_attention' ? (
            <Button
              onClick={() => acknowledge.mutate()}
              disabled={acknowledge.isPending}
              variant="secondary"
            >
              I have seen this
            </Button>
          ) : (
            <p className="rn-muted">Acknowledged. It stays here until it resolves.</p>
          )}

          <label className="rn-col" style={{ gap: 'var(--space-8)' }}>
            <span>Why this does not matter</span>
            <textarea
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setReasonTouched(true);
              }}
              rows={3}
            />
          </label>
          {reasonTouched && reasonTooShort ? (
            <p className="rn-muted">
              A dismissal is a permanent record with your name on it, so it has to say why.
            </p>
          ) : null}
          <Button
            onClick={() => dismiss.mutate()}
            disabled={reasonTooShort || dismiss.isPending}
            variant="secondary"
          >
            Dismiss
          </Button>
          {acknowledge.isError || dismiss.isError ? (
            <MutationFailure
              error={acknowledge.error ?? dismiss.error}
              fallback="We could not record that. Nothing has changed."
              refusedHeading="You cannot triage this finding"
            />
          ) : null}
        </section>
      ) : null}

      {/* The machinery, last, for the reader who asks "why does it say that". */}
      <section className="rn-card">
        <h2 className="rn-card-title">How Phibian arrived at this</h2>
        <dl className="rn-facts">
          <dt>Detector</dt>
          <dd>{finding.detectorId}</dd>
          <dt>Signal</dt>
          <dd>
            <code>{finding.signalId}</code>
          </dd>
          <dt>Confidence</dt>
          <dd>{Math.round(finding.confidence * 100)}% — an inference, never certainty</dd>
          <dt>First run · last run</dt>
          <dd>
            <code>{finding.firstRunId}</code> · <code>{finding.lastRunId}</code>
          </dd>
        </dl>
      </section>
    </article>
  );
}
